import { ChartType, DoughnutChartProps, PolymorphicChart } from "@foodpilot/foods";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n";
import { formatNumber } from "src/utils/formatting";
import { PrevScoreLayout } from "./PrevScoreLayout";
import { DatavizType } from "src/utils/chart";
import { isAllowedChartType } from "src/utils/chart";
import { DimensionValue, MainScore } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { Loading } from "src/components/utils/Loading";
import { Drilldown, DrilldownState } from "src/context/ScoreDetail/useDrilldown";
import { ModalData } from "../../PropertyModal/PropertyModal";
import { type ReactNode, useEffect } from "react";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ScoreService } from "src/services/ScoreService";
import { isAppInPDFMode } from "src/utils/rendering";
import type { FetchPostForAnswers } from "src/context/ScoreDetail/context";

type DoughnutChartAcc = {
    values: number[];
    labels: string[];
    actions: Array<(() => void) | undefined>;
    maxScores: number[];
    dimensionValues: Array<{ dimension: string; id: string | number }>;
};

export type OnLastLevelClickAction = (
    dimension: string,
    dimensionValue: DimensionValue,
    drilldownState: DrilldownState[],
) => void;

export type DistributionChartCoreProps = {
    distributionContent?: ReactNode;
    scoreId: number;
    prevCampaign?: string;
    DoughnutPropsOverride?: Partial<DoughnutChartProps>;
    olapScores?: MainScore;
    olapPrevScores?: MainScore;
    scoresParams?: OlapScoresParams;
    areAggregationsRequested?: boolean;
    getDimensionValueLocalizedLabel?: (dimension: string, key: string | number) => string | null;
    getDimensionValuesColors?: (dimensionValues: Array<{ dimension: string; id: string | number }>) => string[];
    getDimensionValueDrilldownAction?: (
        dimension: string,
        dimensionValue: DimensionValue | undefined,
        onLastLevelClick?: OnLastLevelClickAction,
    ) => (() => void) | undefined;
    isLoading?: boolean;
    drilldownState?: Drilldown;
    onLastLevelClick?: OnLastLevelClickAction;
    openModal?: (property: ModalData) => void;
    fetchPostForAnswers?: FetchPostForAnswers;
    hideSeeMoreDetails?: (dimension: string) => boolean;
};
export const DistributionChartCore = (props: DistributionChartCoreProps) => {
    const {
        scoreId,
        olapScores,
        olapPrevScores,
        scoresParams,
        areAggregationsRequested,
        getDimensionValueLocalizedLabel,
        getDimensionValuesColors,
        getDimensionValueDrilldownAction,
        isLoading,
        drilldownState,
        prevCampaign,
        onLastLevelClick,
        fetchPostForAnswers,
        hideSeeMoreDetails,
        distributionContent,
    } = props;

    const { t } = useTranslation();
    const theme = useTheme();
    const { allScoresWithVariations } = useEntityScores();
    const isPdfMode = isAppInPDFMode();

    useEffect(() => {
        scoresParams?.state.setQuery({
            scores: {
                ...scoresParams.query.scores,
                aggregations: undefined,
            },
            prevScores: true,
        });
    }, []);

    if (distributionContent !== undefined) return distributionContent;

    if (isLoading || areAggregationsRequested) return <Loading />;

    const gradeSubscore = OlapScoresService.getSubscoreWithDisplayMode("grade", olapScores);

    const dimension = OlapScoresService.getDimension(drilldownState?.last?.type, olapScores?.dimensions);
    const dimensionValue = OlapScoresService.getDimensionValue(drilldownState?.last?.id, dimension);
    const totalScore = OlapScoresService.normalizeNumericalScore(dimensionValue?.score.value);

    const prevDimension = OlapScoresService.getDimension(drilldownState?.last?.type, olapPrevScores?.dimensions);
    const prevDimensionValue = OlapScoresService.getDimensionValue(drilldownState?.last?.id, prevDimension);
    const prevTotalScore = OlapScoresService.normalizeNumericalScore(prevDimensionValue?.score.value);

    const dimensions = OlapScoresService.getDimensionValueDimensions(
        olapScores,
        drilldownState?.last?.type,
        drilldownState?.last?.id,
    );
    const gradeSubscoreDimensions = OlapScoresService.getDimensionValueDimensions(
        gradeSubscore,
        drilldownState?.last?.type,
        drilldownState?.last?.id,
    );

    const dimensionValuesPerDimension = removeHeadingsNestedUnderSsqVersions(
        OlapScoresService.flattenDimensionValuesPerDimension(dimensions),
        dimensions,
    );
    const gradeDimensionValuesPerDimension = removeHeadingsNestedUnderSsqVersions(
        OlapScoresService.flattenDimensionValuesPerDimension(gradeSubscoreDimensions),
        dimensions,
    );

    const chartAcc: DoughnutChartAcc = {
        values: [],
        labels: [],
        actions: [],
        maxScores: [],
        dimensionValues: [],
    };

    const scoreWithVariation = ScoreService.getScoreWithVariationById(scoreId, allScoresWithVariations);
    const datavizType = (scoreWithVariation?.datavizType as DatavizType | undefined) ?? undefined;

    const chartTypeValue: ChartType | undefined =
        datavizType && isAllowedChartType(datavizType) ? datavizType : undefined;

    const chartValues =
        dimensionValuesPerDimension ?
            Object.entries(dimensionValuesPerDimension ?? {}).reduce((acc, [dimension, dimensionValues]) => {
                const gradeDimensionValues = gradeDimensionValuesPerDimension?.[dimension];

                dimensionValues.map((dimensionValue) => {
                    const dimensionValueId = dimensionValue.id;
                    const score = dimensionValue.score.value;
                    const max = gradeDimensionValues?.find((value) => value.id === dimensionValueId)?.score.value;

                    if (typeof score !== "number") return;

                    const label =
                        getDimensionValueLocalizedLabel?.(dimension, dimensionValue.id) ?? dimensionValue.label;

                    const action =
                        (
                            !hideSeeMoreDetails?.(dimension) &&
                            (fetchPostForAnswers !== undefined || dimensionValue.dimensions.length > 0)
                        ) ?
                            getDimensionValueDrilldownAction?.(dimension, dimensionValue, onLastLevelClick)
                        :   undefined;

                    acc["values"].push(score);
                    acc["labels"].push(label);
                    acc["actions"].push(action);
                    acc["dimensionValues"].push({ dimension, id: dimensionValue.id });
                    chartTypeValue === "raw" && acc["maxScores"].push(typeof max === "number" ? max : 0);
                });

                return acc;
            }, chartAcc)
        :   undefined;

    const mainTotalScore = OlapScoresService.normalizeNumericalScore(olapScores?.score.value);
    const mainPrevTotalScore = OlapScoresService.normalizeNumericalScore(olapPrevScores?.score.value);

    const totalScoreValue = drilldownState?.last ? totalScore : mainTotalScore;
    const prevTotalScoreValue = drilldownState?.last ? prevTotalScore : mainPrevTotalScore;

    const _total = formatNumber(totalScoreValue ?? 0, i18n.resolvedLanguage, {
        minimumFractionDigits: scoreWithVariation?.precision,
        maximumFractionDigits: scoreWithVariation?.precision,
    });

    if (chartValues && chartValues.values.length === 0) {
        return (
            <div style={{ textAlign: "center", padding: "20px" }}>
                <p>{t("score_detail.no_data_available", "Aucune donnée disponible pour le graphique")}</p>
                <p>
                    {t("score_detail.total", "Total")}: {_total}
                </p>
            </div>
        );
    }

    return (
        <PrevScoreLayout
            scoreWithVariation={scoreWithVariation}
            total={totalScoreValue}
            prevScore={chartTypeValue !== "raw" ? prevTotalScoreValue : null}
            unit={olapScores?.unit}
            prevCampaignLabel={prevCampaign}
        >
            {chartTypeValue && (
                <PolymorphicChart
                    chartType={chartTypeValue}
                    createPalette={false}
                    colors={
                        (chartValues && getDimensionValuesColors?.(chartValues.dimensionValues)) ||
                        theme.custom.dataviz.onlyMainFromAll
                    }
                    values={chartValues?.values}
                    valuesFormatterFn={(value: number) =>
                        formatNumber(value, i18n.resolvedLanguage, {
                            minimumFractionDigits: scoreWithVariation?.precision,
                            maximumFractionDigits: scoreWithVariation?.precision,
                        })
                    }
                    labels={chartValues?.labels}
                    valuesUnit={olapScores?.unit}
                    fullWidthLegend={true}
                    chartWidth={300}
                    width={"100%"}
                    spacing={2}
                    borderWidth={2}
                    displayTotal={_total}
                    legend={{
                        position: "right",
                        actions: chartValues?.actions,
                        seeMore: t("see_details"),
                        mode: "default",
                    }}
                    maxScore={chartTypeValue === "raw" ? chartValues?.maxScores : undefined}
                    aggregation={{
                        showAggregateToggle: !isPdfMode && chartValues && chartValues.values.length > 1,
                        aggregateToggleLabel: t(
                            "components.chart.aggregate_toggle_label",
                            "Group scores with less than 1%",
                        ),
                        othersCategoryLabel: t("components.chart.others_category_label", "Others"),
                        othersThresholdPercent: 1,
                    }}
                    {...props.DoughnutPropsOverride}
                />
            )}
        </PrevScoreLayout>
    );
};

const removeHeadingsNestedUnderSsqVersions = (
    dimensionValuesPerDimension: Record<string, DimensionValue[]> | undefined,
    dimensions: MainScore["dimensions"] | undefined,
): Record<string, DimensionValue[]> | undefined => {
    const headingIdsUnderSsqVersions = getHeadingIdsUnderSsqVersions(dimensions);

    if (!dimensionValuesPerDimension || headingIdsUnderSsqVersions.size === 0) {
        return dimensionValuesPerDimension;
    }

    return {
        ...dimensionValuesPerDimension,
        headings:
            dimensionValuesPerDimension.headings?.filter((value) => !headingIdsUnderSsqVersions.has(value.id)) ?? [],
    };
};

const getHeadingIdsUnderSsqVersions = (dimensions: MainScore["dimensions"] | undefined): Set<string> => {
    const ssqVersionsDimension = OlapScoresService.getDimension("ssq_versions", dimensions);
    const headingIds = new Set<string>();

    ssqVersionsDimension?.values.forEach((value) => {
        findDimensionValueIds(value.dimensions, "headings").forEach((headingId) => headingIds.add(headingId));
    });

    return headingIds;
};

const findDimensionValueIds = (dimensions: MainScore["dimensions"] | undefined, dimensionName: string): string[] => {
    return (
        dimensions?.flatMap((dimension) => {
            const nestedIds = dimension.values.flatMap((value) =>
                findDimensionValueIds(value.dimensions, dimensionName),
            );

            if (dimension.label !== dimensionName) {
                return nestedIds;
            }

            return [...dimension.values.map((value) => value.id), ...nestedIds];
        }) ?? []
    );
};
